<template>
    <ValidationObserver ref="form" v-slot="{ invalid }">
        <div class="absolute top-0 left-0 right-0 bottom-0 w-full overflow-hidden flex items-center text-center justify-center z-50" v-show="loading"><spinner line-fg-color="#F02629" size="55" :line-size="5"></spinner></div>
        <form id="form" v-if="!loading" @submit.prevent="submitForm" class="flex flex-col h-full">
            <page-header :buttons="pageActions.buttons" :additional="pageActions.additional" @method="methodHandler">
                <template slot="title">{{ type }} Task</template>
            </page-header>

            <tabs :tabs="tabs" :active="startTab" @setTab="setActiveTab" ref="tabs"></tabs>
            <div class="bg-red-200 py-2 text-sm text-red-700 text-center w-full -mt-px" role="alert" v-show="invalid && submitted" >Please complete all required fields</div>
            <div class="bg-red-200 py-2 text-sm text-red-700 text-center w-full -mt-px" v-if="errorMessage">{{ errorMessage }}</div>

            <div class="flex md:flex-row sm:flex-col flex-grow">
                <div class="md:w-3/4 sm:w-full px-4 sm:px-6 lg:px-6 bg-gray-200 border-r border-gray-400 pb-12" v-show="activeTab === 'details'">
                    <ValidationObserver vid="details" :slim="true">
                        <div class="mt-6 grid grid-cols-1 row-gap-5 col-gap-4 sm:grid-cols-12">
                            <input-select class="sm:col-span-3"
                                label="Type"
                                placeholder="Type"
                                name="type"
                                v-model="item.type_id"
                                :initial-value="initialValue.type"
                                return="value"
                                rules="required"
                                :required="true"
                                :options="options.types"/>
                            <input-select class="sm:col-span-3"
                                label="Priority"
                                placeholder="Priority"
                                name="priority"
                                v-model="item.priority_id"
                                :initial-value="initialValue.priority"
                                return="value"
                                rules="required"
                                :required="true"
                                :options="options.priorities"/>
                            <input-select class="sm:col-span-3"
                                label="Status"
                                placeholder="Status"
                                name="status"
                                v-model="item.status_id"
                                :initial-value="initialValue.status"
                                return="value"
                                rules="required"
                                :required="true"
                                :options="options.statuses"/>
                            <input-select class="sm:col-span-3"
                                label="Project"
                                placeholder="Project"
                                name="project"
                                v-model="item.project_id"
                                :initial-value="initialValue.project"
                                return="value"
                                :required="false"
                                :options="options.projects"/>
                            <input-date-time
                                class="sm:col-span-12 md:col-span-12 lg:col-span-6"
                                label="Due Date"
                                placeholder="Due Date"
                                name="due_date"
                                v-model="item.due_date"
                                :initial-value="initialValue.due_date"
                                type="datetime"
                                format="dd/LL/yyyy h:mm a"
                                :auto="true"
                                rules="required"
                                :required="true"
                                :minuteStep="15"
                                :use12Hour="true"/>
                            <input-select class="sm:col-span-6"
                                placeholder="Search ..."
                                label="Assigned To"
                                name="assignee_id"
                                v-model="item.assignee_id"
                                :initial-value="initialValue.assignee"
                                return="value"
                                endpoint="users"
                                type="system"
                                endpointLabelField="full_name_reversed"
                                ref="assignee"
                                rules="required"
                                :required="true"
                            />
                            <input-text class="sm:col-span-12"
                                label="Subject"
                                placeholder="Subject"
                                name="subject"
                                v-model="item.subject"
                                rules="required"
                                :required="true" />
                            <input-textarea class="sm:col-span-12"
                                label="Description"
                                placeholder="Description"
                                name="description"
                                v-model="item.description"
                                :required="false" />
                            <div class="mt-2 col-span-12">
                                <h2 class="w-full sm:col-span-12 py-2 border-b-2 border-charcoal-900 text-left font-medium text-charcoal-900 mb-3">Documents/Files</h2>
                                <span v-for="(file, index) in item.files" class="text-base mb-1 flex flex-row items-center my-2">
                                    <a :href="S3URL + '/' + file.path " target="_blank" class="text-gray-800 hover:text-default-500 truncate">{{ file.filename }}</a>
                                    <button type="button" @click="removeUploadFile(index, 'files')">
                                        <svg aria-hidden="true" focusable="false" data-prefix="far" data-icon="times" class="svg-inline--fa fa-times w-6 h-6 text-red-700 ml-2" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                                            <path fill="currentColor" d="M207.6 256l107.72-107.72c6.23-6.23 6.23-16.34 0-22.58l-25.03-25.03c-6.23-6.23-16.34-6.23-22.58 0L160 208.4 52.28 100.68c-6.23-6.23-16.34-6.23-22.58 0L4.68 125.7c-6.23 6.23-6.23 16.34 0 22.58L112.4 256 4.68 363.72c-6.23 6.23-6.23 16.34 0 22.58l25.03 25.03c6.23 6.23 16.34 6.23 22.58 0L160 303.6l107.72 107.72c6.23 6.23 16.34 6.23 22.58 0l25.03-25.03c6.23-6.23 6.23-16.34 0-22.58L207.6 256z"></path>
                                        </svg>
                                    </button>
                                </span>
                                <FileUpload @uploaded="fileUploaded($event, 'files')"/>
                            </div>
                        </div>
                    </ValidationObserver>
                </div>
                <div class="md:w-1/4 sm:w-full px-4 sm:px-6 lg:px-6">
                    <ValidationObserver vid="details" :slim="true">
                        <div class="mt-6 grid grid-cols-1 row-gap-5 col-gap-4 sm:grid-cols-12">
                            <input-select class="sm:col-span-12"
                                placeholder="Linked Record"
                                label="Linked Record"
                                name="model"
                                v-model="model"
                                :initial-value="initialValue.model"
                                return="other"
                                :options="options.models"
                                @input="setEndpoint" />
                            <input-select v-if="link_endpoint"
                                class="sm:col-span-12"
                                placeholder="Search ..."
                                :label="link_label"
                                name="model_id"
                                v-model="item.model.id"
                                :initial-value="initialValue.entity"
                                return="value"
                                :endpoint="link_endpoint"
                                endpointLabelField="label"
                                rules="required"
                                :required="true"/>
                        </div>
                    </ValidationObserver>
                </div>
            </div>
        </form>
    </ValidationObserver>
</template>

<script>
    import FormSetup from "../../mixins/Tabs";
    import FormButtons from "../../mixins/FormButtons";
    import axios from "axios";

    export default {
        name: "TaskForm",
        props: {
            id: {required: false},
            type: {required: true},
            return: {required: false, default: '/tasks'},
            page: {required: false, default: 'tasks'}
        },
        mixins: [
            FormSetup,
            FormButtons
        ],
        data: function () {
            return {
                resource: 'tasks',
                pageActions: {
                    buttons: [
                        {
                            label: 'Save',
                            icon: 'save',
                            iconClass: 'text-green-600',
                            type: 'method',
                            method: 'save',
                        },
                        {
                            label: 'View',
                            icon: 'eye',
                            iconClass: 'text-blue-600',
                            type: 'method',
                            method: 'view',
                            display: this.type === "Edit"
                        },
                        {
                            label: 'Cancel',
                            icon: 'times',
                            iconClass: 'text-red-600',
                            type: 'method',
                            method: 'cancel',
                        }
                    ]
                },
                tabs: [
                    {
                        name: 'Details',
                        slug: 'details'
                    }
                ],
                startTab: null,
                loading: true,
                submitted: false,
                errorMessage: '',
                item: {
                    model: {}
                },
                S3URL: process.env.VUE_APP_S3_URL,
                link_endpoint: null,
                link_label: null,
                model: null,
                model_id: null,
                options: {
                    types: [],
                    statuses: [],
                    priorities: [],
                    projects: [],
                    models: [
                        { label: 'Submission', value: 'submissions' },
                        { label: 'Member', value: 'members' },
                        { label: 'Supplier', value: 'suppliers' },
                    ]
                }
            }
        },
        methods: {
            setEndpoint: function(payload){

                if(this.item.model && this.item.model.model != payload.label){
                    this.item.model.id = null;
                    this.item.model.model = payload ? payload.label : null;
                }

                this.link_endpoint = payload ? payload.value : null;
                this.link_label = payload ? payload.label : null;

            },
            getData: function () {
                this.$Progress.start();

                axios.get(process.env.VUE_APP_API_URL + '/tasks/' + this.id, {
                    params: {
                        with: 'notes,attachments'
                    }
                })
                .then(response => {
                    this.item = response.data;
                    this.$Progress.finish();
                    this.loading = false;
                })
                .catch(() => {
                    this.$Progress.finish();
                    this.loading = false;
                });
            },
            submitForm: function () {
                this.submitted = false;

                this.$refs.form.validate().then(success => {
                    let _method;
                    let _url;

                    if (!success) {
                        this.submitted = true;
                        return;
                    }

                    this.submitted = true;

                    this.$store.commit('TOGGLE_BACKDROP', {open: true});
                    this.errorMessage = '';

                    if (this.id) {
                        _url = process.env.VUE_APP_API_URL + '/tasks/' + this.id;
                        _method = 'PUT';
                    } else if (this.item.id) {
                        _url = process.env.VUE_APP_API_URL + '/tasks/' + this.item.id;
                        _method = 'PUT';
                    } else {
                        _url = process.env.VUE_APP_API_URL + '/tasks';
                        _method = 'POST';
                    }

                    axios({
                        method: _method,
                        url: _url,
                        data: this.item
                    }).then(response => {
                        if (response.data.success) {
                            this.$store.commit('TOGGLE_BACKDROP', {open: false});

                            this.$router.push('/tasks/'+response.data.data.id+'/view');

                            this.$notify({
                                group: 'main',
                                type: 'success',
                                title: 'Success',
                                text: response.data.message,
                                duration: 5000
                            });

                        } else {
                            if (response.data.message) {
                                this.errorMessage = response.data.message;
                            }
                            this.$store.commit('TOGGLE_BACKDROP', {open: false});
                        }
                    }).catch(error => {
                        if (error.response.data.message) {
                            this.errorMessage = error.response.data.message;
                        } else {
                            this.errorMessage = 'An error occurred. Please contact the System Administrator.';
                        }

                        this.$store.commit('TOGGLE_BACKDROP', {open: false});
                    });
                });
            },
            methodHandler: function (method) {
                this[method]();
            },
            actionHandler: function (data) {
                this[data.method](data);
            },
            fileUploaded(payload, key) {
                if (!this.item[key]){
                    this.item[key] = [];
                }

                payload.data.forEach(file => {
                    this.item[key].push(file)
                })
            },
            removeUploadFile(index, key){
                this.item[key].splice(index, 1)
            },
        },
        beforeMount() {
            // Types
            if (this.$store.getters.taskTypes.length === 0) {
                this.$store.dispatch('GET_TASK_TYPES').then(() => {
                    this.options.types = this.$store.getters.taskTypes.map(function (type) {
                        return {label: type.name, value: type.id};
                    });
                });
            } else {
                this.options.types = this.$store.getters.taskTypes.map(function (type) {
                    return {label: type.name, value: type.id};
                });
            }

            // Priorities
            if (this.$store.getters.taskPriorities.length === 0) {
                this.$store.dispatch('GET_TASK_PRIORITIES').then(() => {
                    this.options.priorities = this.$store.getters.taskPriorities.map(function (type) {
                        return {label: type.name, value: type.id};
                    });
                });
            } else {
                this.options.priorities = this.$store.getters.taskPriorities.map(function (type) {
                    return {label: type.name, value: type.id};
                });
            }

            // Statuses
            if (this.$store.getters.taskStatuses.length === 0) {
                this.$store.dispatch('GET_TASK_STATUSES').then(() => {
                    this.options.statuses = this.$store.getters.taskStatuses.map(function (type) {
                        return {label: type.name, value: type.id};
                    });
                });
            } else {
                this.options.statuses = this.$store.getters.taskStatuses.map(function (type) {
                    return {label: type.name, value: type.id};
                });
            }

            // Projects
            if (this.$store.getters.taskProjects.length === 0) {
                this.$store.dispatch('GET_TASK_PROJECTS').then(() => {
                    this.options.projects = this.$store.getters.taskProjects.filter((project) => project.active).map(function (project) {
                        return {label: project.name, value: project.id};
                    });
                });
            } else {
                this.options.projects = this.$store.getters.taskProjects.filter((project) => project.active).map(function (project) {
                    return {label: project.name, value: project.id};
                });
            }
        },
        mounted() {
            if (this.type === "New") {
                this.loading = false;
                this.$Progress.finish();
                // Se defaults here
            } else if (this.type === "Edit") {
                this.getData();
            }

            let windowLocationHash = window.location.hash.replace('#', '');

            if (windowLocationHash) {
                this.startTab = windowLocationHash;
            }
        },
        computed: {
            initialValue: function(){
                let output = {};

                if (this.item && this.item.status){
                    output['status'] = this.item.status.id;
                } else {
                    if (this.$store.getters.taskStatuses && this.$store.getters.taskStatuses.length > 0) {
                        output['status'] = this.$store.getters.taskStatuses.filter(status => {
                            if (status.name === 'Open') {
                                return status.id;
                            } else {
                                return false;
                            }
                        })[0].id;
                    } else {
                        output['status'] = null
                    }
                }

                if (this.item && this.item.priority){
                    output['priority'] = this.item.priority.id;
                } else {
                    if (this.$store.getters.taskPriorities && this.$store.getters.taskPriorities.length > 0) {
                        output['priority'] = this.$store.getters.taskPriorities.filter(priority => {
                            if (priority.name === 'Normal') {
                                return priority.id;
                            } else {
                                return false;
                            }
                        })[0].id;
                    } else {
                        output['priority'] = null
                    }
                }

                if (this.item && this.item.assignee){
                    output['assignee'] = { value: this.item.assignee.id, label: this.item.assignee.name };
                }

                if (this.item && this.item.type){
                    output['type'] = this.item.type.id;
                }

                if (this.item && this.item.project){
                    output['project'] = this.item.project.id;
                }

                if (this.item && this.item.model && this.item.model.model){
                    const model = this.options.models.find(m => m.label === this.item.model.model);
                    const value = model ? model.value : null;
                    output['model'] = model;
                }

                if (this.item && this.item.model){
                    output['entity'] = { value: this.item.model.id, label: this.item.model.name };
                }

                if (this.item && this.item.due_date){
                    output['due_date'] = this.item.due_date;
                } else {
                    output['due_date'] = null;
                }

                return output;
            },
        }
    }
</script>

<style scoped>

</style>
